import React from "react";
import { useTranslation } from "react-i18next";
import "./ListStates.css";

/** Props for LoadingState component */
export interface LoadingStateProps {
  /** Loading message to display */
  message?: string;
  /** Additional CSS class name */
  className?: string;
}

/**
 * Loading state with spinner animation.
 */
export const LoadingState: React.FC<LoadingStateProps> = ({
  message = "Loading...",
  className = "",
}) => (
  <div
    className={`loading-state ${className}`}
    role="status"
    aria-live="polite"
  >
    <div className="spinner" aria-hidden="true" />
    <p>{message}</p>
  </div>
);

/** Props for EmptyState component */
export interface EmptyStateProps {
  /** Title for the empty state */
  title: string;
  /** Description message */
  message: string;
  /** Optional action button */
  action?: {
    label: string;
    onClick: () => void;
  };
  /** Additional CSS class name */
  className?: string;
}

/**
 * Empty state when no items are found.
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  message,
  action,
  className = "",
}) => (
  <div className={`empty-state ${className}`} role="status">
    <h3>{title}</h3>
    <p>{message}</p>
    {action && (
      <button
        type="button"
        className="btn btn-primary"
        onClick={action.onClick}
      >
        {action.label}
      </button>
    )}
  </div>
);

/** Props for ErrorState component */
export interface ErrorStateProps {
  /** Error title */
  title?: string;
  /** Error message to display */
  message: string;
  /** Optional retry callback */
  onRetry?: () => void;
  /** Additional CSS class name */
  className?: string;
}

/**
 * Error state when data loading fails.
 */
export const ErrorState: React.FC<ErrorStateProps> = ({
  title = "Error",
  message,
  onRetry,
  className = "",
}) => (
  <div className={`empty-state error-state ${className}`} role="alert">
    <h3>{title}</h3>
    <p>{message}</p>
    {onRetry && (
      <button type="button" className="btn btn-secondary" onClick={onRetry}>
        Try Again
      </button>
    )}
  </div>
);
